/**
 * Org admin (`/api/admin/organizations/:id/*`) request + response schemas
 * (issue #275 — multi-tenancy Phase 4).
 *
 * Endpoints shipped in Phase 4 (consumed by the SPA's OrganizationDetailPage):
 *
 *   GET    /api/admin/organizations/:id/members             — list members
 *   PATCH  /api/admin/organizations/:id                     — rename (name / slug)
 *   PATCH  /api/admin/organizations/:id/members/:userId     — promote / demote
 *   DELETE /api/admin/organizations/:id/members/:userId     — remove member
 *
 * Phase 5 (#276) will add the invitation flow on top of these — the wire
 * shapes here are the stable Phase 4↔Phase 5 contract. Adding fields is
 * non-breaking; renaming or removing them requires coordination.
 *
 * Role literals (OWNER | MEMBER) mirror `api/src/orgs/roles.ts` exactly.
 * The schema keeps the literal union narrow at the wire boundary so a
 * typo like "owner" surfaces as 422 instead of being silently demoted.
 */

import { z } from "zod";

/**
 * Role literal accepted by the role-update endpoint. Mirrors
 * `ORG_MEMBER_ROLES` in `api/src/orgs/roles.ts` — kept in sync by hand
 * because the API schema lives server-side per the layering rules in
 * `standards/api-patterns.md`.
 */
export const ORG_MEMBER_ROLE_LITERALS = ["OWNER", "MEMBER"] as const;
export type OrgMemberRoleLiteral = (typeof ORG_MEMBER_ROLE_LITERALS)[number];

/**
 * Body schema for `PATCH /api/admin/organizations/:id`.
 *
 * Both fields are optional but at least one must be present — a no-op
 * PATCH would be a wasted round-trip and an audit-log churn vector. The
 * `.refine` enforces "at least one of name | slug" at the wire boundary
 * so the service never has to defend against an empty payload.
 *
 * `name`  — free text, trimmed, length-bounded so a 10MB blob can't slip in.
 * `slug`  — same charset as `Site.slug` (lowercase alphanumeric + hyphens),
 *           bounded to a sane length. The DB has a global unique constraint
 *           via the Phase-1 migration; the service translates 23505 into
 *           409 `SLUG_CONFLICT`.
 */
export const updateOrganizationSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    slug: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u, {
        message: "Slug must be lowercase alphanumeric with hyphens.",
      })
      .optional(),
  })
  .strict()
  .refine((value) => value.name !== undefined || value.slug !== undefined, {
    message: "At least one of `name` or `slug` is required.",
  });
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

/**
 * Body schema for `PATCH /api/admin/organizations/:id/members/:userId`.
 *
 * Wire shape: `{ role: "OWNER" | "MEMBER" }`. `.strict()` rejects unknown
 * fields so a typo in the SPA (e.g. `newRole` instead of `role`) surfaces
 * as 422 rather than silently dropping the field and turning the call
 * into a no-op.
 */
export const updateOrgMemberRoleSchema = z
  .object({
    role: z.enum(ORG_MEMBER_ROLE_LITERALS),
  })
  .strict();
export type UpdateOrgMemberRoleInput = z.infer<typeof updateOrgMemberRoleSchema>;

/**
 * One row in `GET /api/admin/organizations/:id/members`. The SPA renders
 * the members table from this shape — name + email come from the joined
 * User row, role + joinedAt from the OrganizationMembership row.
 *
 * `name` is nullable because `User.name` is nullable in the schema —
 * pre-Phase-1 signups did not collect a display name. The SPA falls back
 * to the email local-part in that case.
 */
export type OrgMemberResponse = {
  userId: string;
  email: string;
  name: string | null;
  role: OrgMemberRoleLiteral;
  joinedAt: string;
};

/**
 * Response shape for `GET /api/admin/organizations/:id/members`. Plain
 * array under `data` — the endpoint is not paginated because every
 * org-admin operation needs the full list (e.g. counting OWNERs to
 * enforce the last-owner protection client-side, even though the API
 * is the authoritative gate).
 *
 * The org itself is included in the envelope so the detail page can
 * render the heading + slug + rename form without a second round-trip.
 */
export type OrganizationMembersResponse = {
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  members: OrgMemberResponse[];
};

/**
 * Response shape for `PATCH /api/admin/organizations/:id`. Mirror of the
 * persisted row — gives the SPA the canonical name / slug post-write so
 * it doesn't have to optimistically derive them.
 */
export type UpdateOrganizationResponse = {
  id: string;
  name: string;
  slug: string;
};

/**
 * Response shape for `PATCH /api/admin/organizations/:id/members/:userId`.
 * Echoes the persisted membership row.
 */
export type UpdateOrgMemberRoleResponse = OrgMemberResponse;
